import React, { useState } from 'react';
import {
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Tooltip,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { useExportDpsEnrollmentCsvMutation } from '../../store/api/devicesApi';

interface ExportDpsButtonProps {
  selectedDeviceIds?: string[];
  variant?: 'text' | 'outlined' | 'contained';
  size?: 'small' | 'medium' | 'large';
}

export const ExportDpsButton: React.FC<ExportDpsButtonProps> = ({
  selectedDeviceIds,
  variant = 'contained',
  size = 'medium',
}) => {
  const [exportCsv, { isLoading }] = useExportDpsEnrollmentCsvMutation();
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const handleExport = async () => {
    try {
      const result = await exportCsv({ deviceIds: selectedDeviceIds }).unwrap();

      // Create download link
      const url = window.URL.createObjectURL(result);
      const link = document.createElement('a');
      link.href = url;

      // Generate filename with timestamp
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      link.download = `dps_enrollment_${timestamp}.csv`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      const count = selectedDeviceIds?.length || 'all';
      setSnackbar({
        open: true,
        message: `Successfully exported ${count} device${count === 1 ? '' : 's'} for DPS enrollment`,
        severity: 'success',
      });
    } catch (error: any) {
      console.error('Export failed:', error);
      setSnackbar({
        open: true,
        message: error?.data?.detail || 'Failed to export DPS enrollment data',
        severity: 'error',
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const tooltipText = selectedDeviceIds?.length
    ? `Export ${selectedDeviceIds.length} selected device${selectedDeviceIds.length === 1 ? '' : 's'}`
    : 'Export all TPM-enabled devices';

  return (
    <>
      <Tooltip title={tooltipText}>
        <span>
          <Button
            variant={variant}
            size={size}
            onClick={handleExport}
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={20} /> : <DownloadIcon />}
          >
            {isLoading ? 'Exporting...' : 'Export DPS CSV'}
          </Button>
        </span>
      </Tooltip>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};
